/*
 * Fetches every review for the current product from Judge.me and renders them
 * into the PDP review section.
 *
 * Endpoint: https://cdn.judge.me/reviews/reviews_for_widget
 *   url / shop_domain : the .myshopify.com domain
 *   platform          : shopify
 *   product_id        : Shopify product id
 *   per_page          : honoured, but Judge.me caps it at 30
 *   page              : 1-based
 *
 * This is the same public endpoint Judge.me's own widget calls. It needs no
 * API token and responds with `Access-Control-Allow-Origin: *`, so there is no
 * credential to leak and no proxy required. The payload is
 * { html, total_count, page } where `html` is the rendered review markup.
 *
 * Reviews are submitted back to https://api.judge.me/api/v1/reviews - also the
 * endpoint Judge.me's own form posts to, also tokenless for creating (only
 * reading the API needs a key), as multipart form data:
 *   url / shop_domain    : the .myshopify.com domain
 *   platform             : shopify
 *   id                   : Shopify product id
 *   name / email         : the reviewer
 *   shopify_customer_id  : lets Judge.me tie the review to the account
 *   rating / title / body
 *   picture_keys[0..n]   : S3 keys of any photos, see below
 * Who is allowed to see the form at all is decided in Liquid, against the
 * signed-in customer's order history - not here.
 *
 * Photos do not travel through that request. Judge.me hands out a presigned S3
 * POST from /api/v1/pictures/presigned_data ({ url, key_prefix, fields }), each
 * file goes straight to S3 under `key_prefix + filename`, and the review then
 * references the keys S3 hands back. The presigned fields carry
 * success_action_status=201, which is what makes S3 answer with the XML body
 * holding <Key>.
 */
(function () {
    'use strict';

    var ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
    var SUBMIT = 'https://api.judge.me/api/v1/reviews';
    var PRESIGN = 'https://api.judge.me/api/v1/pictures/presigned_data';
    var MAX_PICS = 5;                      /* Judge.me's own widget caps at 5 */
    var MAX_PIC_BYTES = 10 * 1024 * 1024;  /* ...and 10MB each */
    var PER_PAGE = 30;  /* Judge.me's ceiling; asking for more silently yields 30 */
    var CONCURRENCY = 3;
    var MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    function text(el) {
        return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }

    function num(value) {
        var n = parseFloat(value);
        return isNaN(n) ? null : n;
    }

    function initials(name) {
        var parts = String(name || '').split(/\s+/).filter(Boolean).slice(0, 2);
        if (!parts.length) return '?';
        return parts.map(function (p) { return p.charAt(0).toUpperCase(); }).join('');
    }

    function shortDate(raw) {
        if (!raw) return '';
        var d = new Date(raw);
        if (isNaN(d.getTime())) return '';
        return String(d.getDate()).padStart(2, '0') + ' ' + MONTHS[d.getMonth()];
    }

    function stars(score) {
        var pct = Math.max(0, Math.min(5, num(score) || 0)) / 5 * 100;
        var wrap = document.createElement('span');
        wrap.className = 'pr-stars';
        wrap.style.setProperty('--pr-fill', pct.toFixed(2) + '%');
        wrap.appendChild(document.createElement('span')).className = 'pr-stars__fill';
        return wrap;
    }

    /* Judge.me custom-form answers are the only place a "Size" can live, and
       the labels are merchant-defined, so match loosely and give up quietly. */
    function customField(rev, wanted) {
        var nodes = rev.querySelectorAll('.jdgm-rev__custom-form *');
        for (var i = 0; i < nodes.length; i++) {
            var m = text(nodes[i]).match(/^([^:]{1,30}):\s*(.+)$/);
            if (m && m[1].toLowerCase().indexOf(wanted) !== -1) return m[2].trim();
        }
        return '';
    }

    /* Judge.me hands us markup, so only absolute http(s) (or protocol-relative)
       image URLs are trusted - never a javascript: or data: href. */
    function safeUrl(raw) {
        var s = String(raw || '').trim();
        if (!s) return '';
        if (s.indexOf('//') === 0 || /^https?:\/\//i.test(s)) return s;
        return '';
    }

    /* Review photos are lazy-loaded by Judge.me's own widget, so the thumbnail
       lives in img[data-src] rather than src, and the full-size copy in the
       link's data-mfp-src (href is the untouched original, as a fallback).
       .jdgm-rev__product-picture is the product shot, not a customer photo. */
    function reviewPics(rev) {
        var links = rev.querySelectorAll('.jdgm-rev__pics .jdgm-rev__pic-link:not(.jdgm-rev__product-picture)');
        var out = [];

        for (var i = 0; i < links.length; i++) {
            var img = links[i].querySelector('img.jdgm-rev__pic-img');
            var thumb = safeUrl(img && (img.getAttribute('data-src') || img.getAttribute('src')));
            var full = safeUrl(links[i].getAttribute('data-mfp-src')) || safeUrl(links[i].getAttribute('href'));
            if (thumb || full) out.push({ thumb: thumb || full, full: full || thumb });
        }
        return out;
    }

    function parseReview(rev) {
        var stamp = rev.querySelector('.jdgm-rev__timestamp');
        var rating = rev.querySelector('.jdgm-rev__rating');

        /* The <time> element renders empty - the date lives in its attributes. */
        var when = '';
        if (stamp) when = stamp.getAttribute('datetime') || stamp.getAttribute('data-content') || '';

        var score = num(rev.getAttribute('data-score'));
        if (score === null && rating) score = num(rating.getAttribute('data-score'));

        var title = text(rev.querySelector('.jdgm-rev__title'));
        var body = text(rev.querySelector('.jdgm-rev__body')) || title;

        return {
            id: rev.getAttribute('data-review-id') || '',
            author: text(rev.querySelector('.jdgm-rev__author')) || 'Verified buyer',
            location: text(rev.querySelector('.jdgm-rev__location')),
            date: shortDate(when),
            score: score === null ? 5 : score,
            title: title === body ? '' : title,
            body: body,
            verified: rev.getAttribute('data-verified-buyer') === 'true',
            size: customField(rev, 'size'),
            pics: reviewPics(rev)
        };
    }

    function stampText(r) {
        var bits = [];
        if (r.verified) bits.push('Verified purchase');
        if (r.size) bits.push('Size ' + r.size);
        return bits.join(' — ');
    }

    function buildCard(r) {
        var card = document.createElement('article');
        card.className = 'pr-card';

        var head = document.createElement('div');
        head.className = 'pr-card__head';

        var av = document.createElement('span');
        av.className = 'pr-avatar';
        av.setAttribute('aria-hidden', 'true');
        av.textContent = initials(r.author);
        head.appendChild(av);

        var id = document.createElement('div');
        id.className = 'pr-card__id';
        var name = document.createElement('div');
        name.className = 'pr-card__name';
        name.textContent = r.author;
        id.appendChild(name);

        var bits = [r.location, r.date].filter(Boolean);
        if (bits.length) {
            var meta = document.createElement('div');
            meta.className = 'pr-card__meta';
            meta.textContent = bits.join(' — ');
            id.appendChild(meta);
        }
        head.appendChild(id);

        var st = stars(r.score);
        st.setAttribute('role', 'img');
        st.setAttribute('aria-label', r.score + ' out of 5 stars');
        head.appendChild(st);
        card.appendChild(head);

        var wrap = document.createElement('div');
        wrap.className = 'pr-card__text';

        var p = document.createElement('p');
        p.className = 'pr-card__body';
        p.textContent = r.body;
        wrap.appendChild(p);

        /* Rendered for every card but revealed only once layout says the body
           really is clipped - see syncMore. */
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'pr-card__more';
        more.hidden = true;
        more.textContent = 'Show more';
        more.setAttribute('aria-label', 'Read ' + r.author + '\u2019s full review');
        more.addEventListener('click', function () { lightbox.open(r, 0, more); });
        wrap.appendChild(more);

        card.appendChild(wrap);

        var stamp = stampText(r);
        if (stamp || r.pics.length) {
            var f = document.createElement('div');
            f.className = 'pr-card__foot';

            var label = document.createElement('span');
            label.className = 'pr-card__stamp';
            label.textContent = stamp;
            f.appendChild(label);

            if (r.pics.length) f.appendChild(buildThumb(r));
            card.appendChild(f);
        }

        return card;
    }

    /* The body is clamped to three lines in CSS, so whether a review actually
       needs a "Show more" can only be settled once it has been laid out - and
       the answer changes again when the card width does, because mobile cards
       are sized as a share of the viewport. */
    function syncMore(scope) {
        var texts = scope.querySelectorAll('.pr-card__text');

        for (var i = 0; i < texts.length; i++) {
            var body = texts[i].querySelector('.pr-card__body');
            var more = texts[i].querySelector('.pr-card__more');
            if (body && more) more.hidden = body.scrollHeight <= body.clientHeight + 1;
        }
    }

    /* The thumbnail sits on the verified-purchase line; any further photos are
       counted on it and reached from inside the lightbox. */
    function buildThumb(r) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pr-card__thumb';
        btn.setAttribute('aria-label', 'View photo from ' + r.author + '’s review');

        var img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        /* A dead thumbnail would otherwise leave a broken-image box on the card. */
        img.addEventListener('error', function () {
            if (btn.parentNode) btn.parentNode.removeChild(btn);
        });
        img.src = r.pics[0].thumb;
        btn.appendChild(img);

        if (r.pics.length > 1) {
            var more = document.createElement('span');
            more.className = 'pr-card__thumb-more';
            more.textContent = '+' + (r.pics.length - 1);
            btn.appendChild(more);
        }

        btn.addEventListener('click', function () { lightbox.open(r, 0, btn); });
        return btn;
    }

    /* One lightbox is shared by every card on the page: the photo at full size
       alongside the review's untruncated text. */
    var lightbox = (function () {
        var el, panel, mediaEl, imgEl, counter, prevBtn, nextBtn, closeBtn, nameEl,
            metaEl, scoreEl, titleEl, bodyEl, footEl, stripEl, reviewEl;
        var formEl, formAs, formFields, formTitle, formBody, formError, formDone,
            formSubmit, formStars, formFile, formThumbs;
        var pics = [];
        var presign = null;   /* cached per popup session; it is time-stamped */
        var picSeq = 0;
        var review = null;
        var index = 0;
        var restoreFocus = null;
        var writeCtx = null;

        function part(parent, tag, cls) {
            var node = parent.appendChild(document.createElement(tag));
            if (cls) node.className = cls;
            return node;
        }

        function navButton(parent, dir, label, glyph) {
            var b = part(parent, 'button', 'pr-lb__nav pr-lb__nav--' + dir);
            b.type = 'button';
            b.setAttribute('aria-label', label);
            b.textContent = glyph;
            b.addEventListener('click', function () { step(dir === 'prev' ? -1 : 1); });
            return b;
        }

        function build() {
            el = document.createElement('div');
            el.className = 'pr-lb';
            el.hidden = true;

            part(el, 'div', 'pr-lb__backdrop').addEventListener('click', close);

            panel = part(el, 'div', 'pr-lb__panel');
            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');

            closeBtn = part(panel, 'button', 'pr-lb__close');
            closeBtn.type = 'button';
            closeBtn.setAttribute('aria-label', 'Close');
            closeBtn.textContent = '\u00D7';
            closeBtn.addEventListener('click', close);

            mediaEl = part(panel, 'div', 'pr-lb__media');
            prevBtn = navButton(mediaEl, 'prev', 'Previous photo', '\u2039');
            imgEl = part(mediaEl, 'img', 'pr-lb__img');
            imgEl.alt = '';
            imgEl.decoding = 'async';
            nextBtn = navButton(mediaEl, 'next', 'Next photo', '\u203A');
            counter = part(mediaEl, 'span', 'pr-lb__counter');

            var info = part(panel, 'div', 'pr-lb__info');

            reviewEl = part(info, 'div', 'pr-lb__review');
            var head = part(reviewEl, 'div', 'pr-lb__head');
            var id = part(head, 'div', 'pr-lb__id');
            nameEl = part(id, 'div', 'pr-lb__name');
            metaEl = part(id, 'div', 'pr-lb__meta');
            scoreEl = part(head, 'span', 'pr-lb__score');

            titleEl = part(reviewEl, 'h3', 'pr-lb__title');
            bodyEl = part(reviewEl, 'p', 'pr-lb__body');
            footEl = part(reviewEl, 'div', 'pr-lb__foot');
            stripEl = part(reviewEl, 'div', 'pr-lb__strip');

            buildForm(info);

            document.body.appendChild(el);
        }

        function buildForm(info) {
            formEl = part(info, 'div', 'pr-lb__form');
            formEl.hidden = true;

            part(formEl, 'h3', 'pr-form__heading').textContent = 'Write a review';
            formAs = part(formEl, 'p', 'pr-form__as');

            var form = part(formEl, 'form', 'pr-form');
            form.setAttribute('novalidate', 'novalidate');
            form.addEventListener('submit', submitReview);

            formFields = part(form, 'div', 'pr-form__fields');
            buildStars(formFields);

            var tWrap = part(formFields, 'label', 'pr-form__field');
            part(tWrap, 'span', 'pr-form__label').textContent = 'Title (optional)';
            formTitle = part(tWrap, 'input', 'pr-form__input');
            formTitle.type = 'text';
            formTitle.maxLength = 120;

            var bWrap = part(formFields, 'label', 'pr-form__field');
            part(bWrap, 'span', 'pr-form__label').textContent = 'Your review';
            formBody = part(bWrap, 'textarea', 'pr-form__input pr-form__textarea');
            formBody.rows = 5;
            formBody.maxLength = 2000;

            var pWrap = part(formFields, 'div', 'pr-form__field');
            part(pWrap, 'span', 'pr-form__label').textContent = 'Photos (optional)';

            var pick = part(pWrap, 'label', 'pr-form__file');
            pick.textContent = 'Add photos';
            formFile = part(pick, 'input', 'pr-form__file-input');
            formFile.type = 'file';
            formFile.accept = 'image/*';
            formFile.multiple = true;
            formFile.addEventListener('change', function () {
                addPictures(formFile.files);
                /* Cleared so re-picking the same file fires change again. */
                formFile.value = '';
            });

            part(pWrap, 'p', 'pr-form__hint').textContent =
                'Up to ' + MAX_PICS + ' images, ' + (MAX_PIC_BYTES / 1024 / 1024) + 'MB each.';
            formThumbs = part(pWrap, 'ul', 'pr-form__thumbs');

            formError = part(form, 'p', 'pr-form__error');
            formError.hidden = true;
            formError.setAttribute('role', 'alert');

            formSubmit = part(form, 'button', 'pr-form__submit');
            formSubmit.type = 'submit';
            formSubmit.textContent = 'Submit review';

            formDone = part(formEl, 'p', 'pr-form__done');
            formDone.hidden = true;
            formDone.setAttribute('role', 'status');
        }

        /* Radios rather than clickable spans, so the rating is reachable and
           settable from the keyboard for free. */
        function buildStars(parent) {
            var set = part(parent, 'fieldset', 'pr-form__stars');
            part(set, 'legend', 'pr-form__label').textContent = 'Your rating';
            var row = part(set, 'div', 'pr-form__stars-row');

            formStars = [];
            for (var n = 1; n <= 5; n++) {
                var input = part(row, 'input', 'pr-form__star-input');
                input.type = 'radio';
                input.name = 'pr-form-score';
                input.value = String(n);
                input.id = 'pr-form-score-' + n;
                input.addEventListener('change', paintStars);

                var label = part(row, 'label', 'pr-form__star');
                label.setAttribute('for', input.id);
                label.setAttribute('aria-label', n === 1 ? '1 star' : n + ' stars');
                label.textContent = '\u2605';

                formStars.push({ input: input, label: label });
            }
        }

        function rating() {
            for (var i = 0; i < formStars.length; i++) {
                if (formStars[i].input.checked) return i + 1;
            }
            return 0;
        }

        function paintStars() {
            var picked = rating();
            formStars.forEach(function (s, i) {
                s.label.classList.toggle('is-on', i < picked);
            });
        }

        /* Only identical names inside one submission can collide, because
           key_prefix is already unique per presign - a counter settles it. */
        function s3Name(file) {
            var raw = String(file.name || 'photo.jpg').replace(/[^a-z0-9\-_.]/gi, '');
            var bits = raw.split('.');
            var ext = bits.length > 1 ? bits.pop() : 'jpg';
            var stem = bits.join('') || String(Date.now());
            return encodeURIComponent(stem + '-' + (++picSeq) + '.' + ext);
        }

        function presignData() {
            if (presign) return presign;

            presign = fetch(PRESIGN + '?url=' + encodeURIComponent(writeCtx.shop) +
                '&shop_domain=' + encodeURIComponent(writeCtx.shop) +
                '&platform=shopify', { credentials: 'omit' }
            ).then(function (res) {
                if (!res.ok) throw new Error('presign responded ' + res.status);
                return res.json();
            }).then(function (data) {
                if (!data || !data.url || !data.fields) throw new Error('presign payload incomplete');
                return data;
            });

            /* A failed presign must not be cached, or every later attempt in
               this popup would fail with it. */
            presign.catch(function () { presign = null; });
            return presign;
        }

        function addPictures(files) {
            var list = Array.prototype.slice.call(files || []);
            if (!list.length) return;

            formError.hidden = true;

            for (var i = 0; i < list.length; i++) {
                var file = list[i];

                if (pics.length >= MAX_PICS) {
                    fail('You can add up to ' + MAX_PICS + ' photos.');
                    break;
                }
                if (file.type.indexOf('image/') !== 0) {
                    fail('\u201C' + file.name + '\u201D is not an image.');
                    continue;
                }
                if (file.size > MAX_PIC_BYTES) {
                    fail('\u201C' + file.name + '\u201D is larger than ' +
                        (MAX_PIC_BYTES / 1024 / 1024) + 'MB.');
                    continue;
                }
                startUpload(file);
            }
        }

        function startUpload(file) {
            var pic = { file: file, key: '', status: 'uploading', preview: '' };

            var li = part(formThumbs, 'li', 'pr-form__thumb is-uploading');
            pic.node = li;

            var img = part(li, 'img');
            img.alt = '';
            if (window.URL && window.URL.createObjectURL) {
                pic.preview = window.URL.createObjectURL(file);
                img.src = pic.preview;
            }

            var drop = part(li, 'button', 'pr-form__thumb-drop');
            drop.type = 'button';
            drop.setAttribute('aria-label', 'Remove ' + file.name);
            drop.textContent = '\u00D7';
            drop.addEventListener('click', function () { dropPicture(pic); });

            pics.push(pic);
            syncSubmit();

            presignData().then(function (data) {
                if (pic.status === 'dropped') return null;

                var key = data.key_prefix + s3Name(file);
                var body = new FormData();
                body.append('key', key);
                Object.keys(data.fields).forEach(function (name) {
                    body.append(name, data.fields[name]);
                });
                body.append('file', file);

                /* Straight to S3, no headers - the presigned fields are the
                   whole authorisation. */
                return fetch(data.url, { method: 'POST', body: body, credentials: 'omit' })
                    .then(function (res) {
                        if (!res.ok) throw new Error('S3 responded ' + res.status);
                        return res.text();
                    })
                    .then(function (xml) { return { key: keyFromS3(xml) || key }; });
            }).then(function (result) {
                if (!result || pic.status === 'dropped') return;
                pic.key = result.key;
                pic.status = 'done';
                li.classList.remove('is-uploading');
                syncSubmit();
            }).catch(function (err) {
                if (pic.status === 'dropped') return;
                pic.status = 'error';
                li.classList.remove('is-uploading');
                li.classList.add('is-error');
                fail('\u201C' + file.name + '\u201D could not be uploaded.');
                syncSubmit();
                if (window.console) console.warn('[pdp-reviews] photo upload failed:', err);
            });
        }

        /* S3 answers a 201 with XML describing what it stored. */
        function keyFromS3(xml) {
            try {
                var doc = new DOMParser().parseFromString(xml || '', 'text/xml');
                var node = doc.getElementsByTagName('Key')[0];
                return node ? (node.textContent || '').trim() : '';
            } catch (e) {
                return '';
            }
        }

        function dropPicture(pic) {
            pic.status = 'dropped';
            if (pic.preview && window.URL && window.URL.revokeObjectURL) {
                window.URL.revokeObjectURL(pic.preview);
            }
            if (pic.node && pic.node.parentNode) pic.node.parentNode.removeChild(pic.node);

            var at = pics.indexOf(pic);
            if (at !== -1) pics.splice(at, 1);
            syncSubmit();
        }

        function clearPictures() {
            pics.slice().forEach(dropPicture);
            pics = [];
            picSeq = 0;
            if (formThumbs) formThumbs.textContent = '';
        }

        function uploading() {
            return pics.some(function (p) { return p.status === 'uploading'; });
        }

        /* Submitting mid-upload would drop the photos silently, so the button
           says what it is waiting for instead. */
        function syncSubmit() {
            if (!formSubmit || formSubmit.hidden) return;
            var busy = uploading();
            formSubmit.disabled = busy;
            formSubmit.textContent = busy ? 'Uploading photos\u2026' : 'Submit review';
        }

        function resetForm() {
            clearPictures();
            presign = null;
            formStars.forEach(function (s) { s.input.checked = false; });
            paintStars();
            formTitle.value = '';
            formBody.value = '';
            formError.hidden = true;
            formDone.hidden = true;
            formFields.hidden = false;
            formSubmit.hidden = false;
            formSubmit.disabled = false;
            formSubmit.textContent = 'Submit review';
        }

        function fail(message) {
            formError.textContent = message;
            formError.hidden = false;
        }

        function submitReview(e) {
            e.preventDefault();
            if (!writeCtx) return;

            var score = rating();
            var body = formBody.value.trim();

            if (!score) { fail('Please choose a star rating.'); return; }
            if (!body) { fail('Please write a few words about the product.'); return; }
            if (uploading()) { fail('Please wait for your photos to finish uploading.'); return; }

            formError.hidden = true;
            formSubmit.disabled = true;
            formSubmit.textContent = 'Submitting\u2026';

            var data = new FormData();
            data.append('url', writeCtx.shop);
            data.append('shop_domain', writeCtx.shop);
            data.append('platform', 'shopify');
            data.append('id', writeCtx.productId);
            data.append('name', writeCtx.name);
            data.append('email', writeCtx.email);
            if (writeCtx.customerId) data.append('shopify_customer_id', writeCtx.customerId);
            data.append('rating', String(score));
            data.append('title', formTitle.value.trim());
            data.append('body', body);

            /* Only the ones S3 actually took; a failed or removed photo must
               not leave a dangling key on the review. */
            pics.filter(function (p) { return p.status === 'done' && p.key; })
                .forEach(function (p, i) { data.append('picture_keys[' + i + ']', p.key); });

            /* Deliberately no headers. A multipart POST carrying none stays a
               CORS-simple request; api.judge.me answers no preflight at all
               (OPTIONS 404s), so adding even an Accept header would make the
               browser ask for one and the submission would never leave. */
            fetch(SUBMIT, { method: 'POST', body: data, credentials: 'omit' }).then(function (res) {
                if (!res.ok) throw new Error('Judge.me responded ' + res.status);
                succeed();
            }).catch(function (err) {
                formSubmit.disabled = false;
                formSubmit.textContent = 'Submit review';
                fail('Sorry, your review could not be sent. Please try again.');
                if (window.console) console.warn('[pdp-reviews] review submit failed:', err);
            });
        }

        function succeed() {
            formFields.hidden = true;
            formSubmit.hidden = true;
            formError.hidden = true;
            formDone.textContent = 'Thank you \u2014 your review has been submitted, and appears once it has been checked.';
            formDone.hidden = false;

            /* Keeps the same page view from sending a second copy. */
            if (writeCtx.trigger) {
                writeCtx.trigger.disabled = true;
                writeCtx.trigger.textContent = 'Review submitted';
            }
        }

        function setMode(mode) {
            reviewEl.hidden = mode !== 'review';
            formEl.hidden = mode !== 'form';
        }

        function paintStrip() {
            stripEl.textContent = '';
            stripEl.hidden = review.pics.length < 2;
            if (stripEl.hidden) return;

            review.pics.forEach(function (pic, i) {
                var b = part(stripEl, 'button', 'pr-lb__dot' + (i === index ? ' is-active' : ''));
                b.type = 'button';
                b.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + review.pics.length);
                var t = part(b, 'img');
                t.alt = '';
                t.loading = 'lazy';
                t.src = pic.thumb;
                b.addEventListener('click', function () { show(i); });
            });
        }

        function show(i) {
            index = (i + review.pics.length) % review.pics.length;
            imgEl.src = review.pics[index].full;
            imgEl.alt = 'Photo ' + (index + 1) + ' from ' + review.author + '’s review';

            var many = review.pics.length > 1;
            counter.hidden = !many;
            prevBtn.hidden = !many;
            nextBtn.hidden = !many;
            if (many) counter.textContent = (index + 1) + ' / ' + review.pics.length;

            paintStrip();
        }

        /* A "Show more" click brings no photos with it, so the whole media
           column steps aside and the panel becomes a single text column. */
        function paintMedia(i) {
            var count = review.pics.length;

            mediaEl.hidden = !count;
            el.classList.toggle('pr-lb--no-photo', !count);

            if (count) {
                show(i);
                return;
            }
            imgEl.removeAttribute('src');
            counter.hidden = true;
            prevBtn.hidden = true;
            nextBtn.hidden = true;
            stripEl.hidden = true;
        }

        function step(delta) {
            if (review && review.pics.length > 1) show(index + delta);
        }

        function inherit(source) {
            if (!source) return;
            var css = getComputedStyle(source);
            ['--pr-star', '--pr-bar', '--pr-verified'].forEach(function (name) {
                var value = css.getPropertyValue(name).trim();
                if (value) el.style.setProperty(name, value);
            });
        }

        function onKey(e) {
            if (e.key === 'Escape') { close(); return; }
            if (e.key === 'ArrowLeft') step(-1);
            if (e.key === 'ArrowRight') step(1);
        }

        /* `trigger` is the thumbnail that opened us: it tells us which section's
           colours to borrow, and where to put focus back on close - Safari does
           not focus a button on click, so document.activeElement will not do. */
        function open(r, i, trigger) {
            if (!el) build();

            inherit(trigger && trigger.closest ? trigger.closest('.pr-reviews') : null);
            review = r;
            writeCtx = null;
            restoreFocus = trigger;
            setMode('review');
            panel.setAttribute('aria-label', r.author + '\u2019s review');

            nameEl.textContent = r.author;
            metaEl.textContent = [r.location, r.date].filter(Boolean).join(' — ');
            metaEl.hidden = !metaEl.textContent;

            scoreEl.textContent = '';
            var st = stars(r.score);
            st.setAttribute('role', 'img');
            st.setAttribute('aria-label', r.score + ' out of 5 stars');
            scoreEl.appendChild(st);

            titleEl.textContent = r.title;
            titleEl.hidden = !r.title;
            bodyEl.textContent = r.body;
            bodyEl.hidden = !r.body;

            footEl.textContent = stampText(r);
            footEl.hidden = !footEl.textContent;

            paintMedia(i || 0);

            el.hidden = false;
            document.documentElement.classList.add('pr-lb-lock');
            document.addEventListener('keydown', onKey);
            closeBtn.focus();
        }

        /* Focus has to land somewhere real. A trigger we disabled on success
           cannot take it, so fall back to the block around it - the customer
           ends up beside the outcome rather than at the top of the page. */
        function returnFocus() {
            if (!restoreFocus || !restoreFocus.focus) return;

            if (!restoreFocus.disabled) {
                restoreFocus.focus();
                return;
            }

            var fallback = restoreFocus.closest('.pr-write') || restoreFocus.parentNode;
            if (!fallback || !fallback.focus) return;
            fallback.setAttribute('tabindex', '-1');
            fallback.focus();
        }

        function close() {
            if (!el || el.hidden) return;
            el.hidden = true;
            /* Drop the source so a large image stops decoding behind the scenes. */
            imgEl.removeAttribute('src');
            review = null;
            document.documentElement.classList.remove('pr-lb-lock');
            document.removeEventListener('keydown', onKey);
            returnFocus();
            restoreFocus = null;
        }

        /* The form borrows the whole popup - backdrop, scroll lock, escape and
           the phone's rise-from-the-bottom - and just swaps what is inside it. */
        function openForm(trigger) {
            var root = trigger.closest('.pr-reviews');
            var write = trigger.closest('.pr-write');
            if (!root || !write) return;

            if (!el) build();
            inherit(root);

            writeCtx = {
                shop: root.getAttribute('data-pr-shop'),
                productId: root.getAttribute('data-pr-product'),
                name: write.getAttribute('data-pr-write-name') || '',
                email: write.getAttribute('data-pr-write-email') || '',
                customerId: write.getAttribute('data-pr-write-customer') || '',
                trigger: trigger
            };

            review = null;
            restoreFocus = trigger;

            resetForm();
            formAs.textContent = writeCtx.name ? 'Posting as ' + writeCtx.name : '';
            formAs.hidden = !writeCtx.name;

            setMode('form');
            mediaEl.hidden = true;
            el.classList.add('pr-lb--no-photo');
            panel.setAttribute('aria-label', 'Write a review');

            el.hidden = false;
            document.documentElement.classList.add('pr-lb-lock');
            document.addEventListener('keydown', onKey);
            closeBtn.focus();
        }

        return { open: open, openForm: openForm };
    })();

    function ProductReviews(root) {
        this.root = root;
        this.track = root.querySelector('[data-pr-track]');
        this.shop = root.getAttribute('data-pr-shop');
        this.productId = root.getAttribute('data-pr-product');

        var limit = parseInt(root.getAttribute('data-pr-limit'), 10);
        this.limit = isNaN(limit) || limit < 0 ? 0 : limit;   /* 0 = every review */

        this.seen = {};
        this.rendered = 0;
        this.scores = [];

        if (!this.track || !this.shop || !this.productId) return;
        this.wireWrite();
        this.load();
        this.watchWidth();
    }

    ProductReviews.prototype.wireWrite = function () {
        this.root.querySelectorAll('[data-pr-write]').forEach(function (btn) {
            btn.addEventListener('click', function () { lightbox.openForm(btn); });
        });
    };

    /* The section hides itself when Judge.me returns nothing, which is exactly
       when a customer entitled to write the first review most needs to see the
       button - so reveal a stripped-down version for them instead. */
    ProductReviews.prototype.showEmpty = function () {
        if (!this.root.querySelector('[data-pr-write]')) return;
        this.root.classList.add('pr-reviews--empty');
        this.root.hidden = false;
    };

    /* A webfont swapping in re-wraps the body, and so does a rotation or a
       resize, either of which can add or remove the third clipped line. */
    ProductReviews.prototype.watchWidth = function () {
        var self = this;
        var timer = null;

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(function () { syncMore(self.track); });
        }

        window.addEventListener('resize', function () {
            clearTimeout(timer);
            timer = setTimeout(function () { syncMore(self.track); }, 150);
        });
    };

    ProductReviews.prototype.pageUrl = function (page) {
        return ENDPOINT
            + '?url=' + encodeURIComponent(this.shop)
            + '&shop_domain=' + encodeURIComponent(this.shop)
            + '&platform=shopify'
            + '&product_id=' + encodeURIComponent(this.productId)
            + '&per_page=' + PER_PAGE
            + '&page=' + page;
    };

    ProductReviews.prototype.fetchPage = function (page) {
        return fetch(this.pageUrl(page), { credentials: 'omit' }).then(function (res) {
            if (!res.ok) throw new Error('Judge.me responded ' + res.status);
            return res.json();
        });
    };

    ProductReviews.prototype.parse = function (html) {
        var doc = new DOMParser().parseFromString(html || '', 'text/html');
        var nodes = doc.querySelectorAll('.jdgm-rev');
        var reviews = [];
        for (var i = 0; i < nodes.length; i++) {
            var r = parseReview(nodes[i]);
            if (r.body) reviews.push(r);
        }

        var lastPage = 1;
        var pager = doc.querySelector('.jdgm-paginate');
        if (pager) {
            var last = pager.querySelector('.jdgm-paginate__last-page');
            if (last) {
                lastPage = parseInt(last.getAttribute('data-page'), 10) || 1;
            } else {
                pager.querySelectorAll('[data-page]').forEach(function (a) {
                    var n = parseInt(a.getAttribute('data-page'), 10);
                    if (n > lastPage) lastPage = n;
                });
            }
        }

        return { reviews: reviews, lastPage: lastPage };
    };

    ProductReviews.prototype.load = function () {
        var self = this;

        this.fetchPage(1).then(function (data) {
            var first = self.parse(data.html);
            if (!first.reviews.length) {
                self.showEmpty();
                return;
            }

            if (data.total_count) self.paintCount(data.total_count);
            self.add(first.reviews);
            self.root.hidden = false;
            /* Only now is the section laid out, so this is the first moment a
               clipped body can be told from a short one. */
            syncMore(self.track);

            return self.loadRest(first.lastPage);
        }).then(function () {
            self.paintHistogram();
        }).catch(function (err) {
            /* No reviews to show, but a customer who may write one still gets
               the button; everyone else keeps a hidden section. */
            self.showEmpty();
            if (window.console) console.warn('[pdp-reviews] could not load reviews:', err);
        });
    };

    /* Pages are fetched with bounded concurrency but appended in page order,
       so the cards keep the order Judge.me returned them in. */
    ProductReviews.prototype.loadRest = function (lastPage) {
        var self = this;
        if (lastPage < 2) return Promise.resolve();
        if (this.limit && this.rendered >= this.limit) return Promise.resolve();

        var pages = [];
        for (var p = 2; p <= lastPage; p++) pages.push(p);

        var byPage = {};
        var cursor = 0;

        function worker() {
            if (cursor >= pages.length) return Promise.resolve();
            var page = pages[cursor++];
            return self.fetchPage(page).then(function (data) {
                byPage[page] = self.parse(data.html).reviews;
            }, function () {
                byPage[page] = [];
            }).then(worker);
        }

        var running = [];
        for (var i = 0; i < CONCURRENCY; i++) running.push(worker());

        this.busy(true);
        return Promise.all(running).then(function () {
            self.busy(false);
            pages.forEach(function (page) { self.add(byPage[page] || []); });
        });
    };

    /* Every score counts toward the histogram, even when `limit` stops us
       rendering a card for it. */
    ProductReviews.prototype.add = function (list) {
        var frag = document.createDocumentFragment();
        var added = 0;

        for (var i = 0; i < list.length; i++) {
            var r = list[i];
            var key = r.id || (r.author + '|' + r.body).slice(0, 200);
            if (this.seen[key]) continue;
            this.seen[key] = true;

            this.scores.push(r.score);
            if (this.limit && this.rendered >= this.limit) continue;

            frag.appendChild(buildCard(r));
            this.rendered++;
            added++;
        }

        if (added) {
            this.track.appendChild(frag);
            syncMore(this.track);
        }
        return added;
    };

    ProductReviews.prototype.busy = function (on) {
        this.track.setAttribute('aria-busy', on ? 'true' : 'false');

        if (!on) {
            if (this.spinner && this.spinner.parentNode) this.spinner.parentNode.removeChild(this.spinner);
            return;
        }
        if (!this.spinner) {
            this.spinner = document.createElement('div');
            this.spinner.className = 'pr-card pr-card--loading';
            this.spinner.textContent = 'Loading reviews…';
        }
        this.track.appendChild(this.spinner);
    };

    ProductReviews.prototype.paintCount = function (count) {
        this.root.querySelectorAll('[data-pr-count]').forEach(function (el) {
            el.textContent = count;
        });
    };

    /* With every page fetched this is the exact distribution, not a sample. */
    ProductReviews.prototype.paintHistogram = function () {
        var hist = this.root.querySelector('[data-pr-hist]');
        var counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        var total = 0;

        this.scores.forEach(function (s) {
            var bucket = Math.round(s);
            if (counts[bucket] === undefined) return;
            counts[bucket]++;
            total++;
        });

        if (!total) {
            if (hist) hist.hidden = true;
            return;
        }
        if (hist) hist.hidden = false;

        var root = this.root;
        for (var star = 1; star <= 5; star++) {
            var pct = counts[star] / total * 100;
            var bar = root.querySelector('[data-pr-bar="' + star + '"]');
            var label = root.querySelector('[data-pr-bar-pct="' + star + '"]');
            if (bar) bar.style.width = pct.toFixed(2) + '%';
            if (label) label.textContent = Math.round(pct) + '%';
        }
    };

    function boot() {
        document.querySelectorAll('[data-pr-reviews]').forEach(function (root) {
            if (root.__prReviews) return;
            root.__prReviews = new ProductReviews(root);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
    document.addEventListener('shopify:section:load', boot);
})();
